import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Button, CameraIcon, Flex, Text } from '@hivewatch/dark'

import { Spinner } from '@/components/ui'
import { VideoPlayer, useCameraFeed } from '@/features/video-player'

import type { SalesVideoWallCameraIProps } from '../../constants/salesCameras'

interface IProps {
  camera: SalesVideoWallCameraIProps
}

/**
 * Sales-env fork of VideoWallTile (ENG-32238): the same feed tile without the
 * remove control, so the seeded cameras cannot be taken off the wall (they
 * would not reappear in the picker — Sales has no cloud cameras). The feed
 * resolves through the shared useCameraFeed hook, which serves the canned mp4
 * for these device ids while FeatureFlag.cannedVideo is on.
 */
export const SalesVideoWallTile = ({ camera }: IProps) => {
  const { t } = useTranslation()
  const { url, isLoading, errorMessage, refetch } = useCameraFeed(
    camera.deviceId
  )
  const [isHovered, setIsHovered] = useState(false)

  return (
    <Box
      alignItems='center'
      bg='bg.card'
      borderRadius='md'
      data-testid='video-wall-tile'
      display='flex'
      h='100%'
      justifyContent='center'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      overflow='hidden'
      position='relative'
      w='100%'
    >
      <Flex
        align='center'
        bg='bg.overlay'
        left={0}
        opacity={isHovered ? 1 : 0}
        position='absolute'
        px='12px'
        py='8px'
        right={0}
        top={0}
        transition='opacity 0.15s'
        zIndex={1}
      >
        <Text
          color='text.primary'
          fontSize='13px'
          fontWeight='600'
          overflow='hidden'
          textOverflow='ellipsis'
          whiteSpace='nowrap'
        >
          {camera.name}
        </Text>
      </Flex>

      {isLoading && <Spinner size='lg' />}

      {!isLoading && errorMessage && (
        <Flex align='center' direction='column' gap='8px' px='16px'>
          <CameraIcon size={28} />
          <Text color='text.secondary' fontSize='13px' textAlign='center'>
            {t('video_wall:feed_error')}
          </Text>
          {/* refetch is absent from the canned stream state; guard so Retry
              can't throw if a seeded device id drifts out of the canned map. */}
          <Button onClick={() => refetch?.()} size='sm' variant='secondary'>
            {t('video_wall:retry')}
          </Button>
        </Flex>
      )}

      {!isLoading && !errorMessage && url && (
        <Box aspectRatio='16 / 9' maxH='100%' maxW='100%' w='100%'>
          <VideoPlayer
            liveOnly
            showControls={false}
            suppressGlobalPlayState
            url={url}
          />
        </Box>
      )}
    </Box>
  )
}
